import { useCallback } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { WarningMessages } from 'constants/Warning.constants';

import {
  type CreateHandoffPayload,
  CreateHandoffSchema,
} from '@polyai/stream/commands/handlers/handoff/create-config/schema';
import { UpdateHandoffSchema } from '@polyai/stream/commands/handlers/handoff/update-config/schema';
import {
  createHandoff,
  selectActiveHandoffs,
  updateHandoff,
} from '@polyai/stream/store/reducers/handoff';
import { NodeTransferIcon } from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import { HANDOFF_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { useBoolean } from 'hooks/useBoolean';
import useTelephony from 'hooks/useTelephony';
import { getActionName } from 'lib/ddlogHelper';
import { FormHandoffModal } from 'screens/Handoff/components/FormHandoffModal';
import { HANDOFF } from 'screens/Handoff/Handoff.uiCopy';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import { switchTagReference } from '../../utils';

const HandoffTagContent = ({
  id,
  closePopup,
  remove,
  nodeKey,
}: ActionTagContentProps) => {
  const [editor] = useLexicalComposerContext();
  const handoffModal = useBoolean();
  const handoffCreateModal = useBoolean();
  const sipModal = useBoolean();

  const serverDispatch = useServerDispatch();
  const activeHandoffs = useAppSelector(selectActiveHandoffs);
  const { allConnectionTokens } = useTelephony();

  const selectedHandoff = activeHandoffs.find(h => h.id === id);
  const hasActiveHandoff = !!selectedHandoff;

  const { isEditable } = useViewMode();

  const handleClose = () => {
    closePopup?.();
    handoffModal.setFalse();
  };

  const handleCreateHandoffConfig = useCallback(
    (data: CreateHandoffPayload) => {
      void serverDispatch(
        createHandoff({
          ...data,
          active: true,
          references: {
            flowSteps: {},
            topics: {},
          },
        }),
      );
      switchTagReference(editor, nodeKey, HANDOFF_PREFIX, data.id);
      handoffCreateModal.setFalse();
    },
    [serverDispatch, handoffCreateModal, editor, nodeKey],
  );

  const connectionToken = allConnectionTokens?.sandbox?.token ?? '';

  const handleCreateHandoff = () => {
    if (!connectionToken) {
      sipModal.setTrue();
    } else {
      handoffCreateModal.setTrue();
    }
  };

  return (
    <>
      {hasActiveHandoff && (
        <PopupMenuItem onClick={handoffModal.setTrue}>
          {isEditable ? 'Edit' : 'View'}
        </PopupMenuItem>
      )}
      {!hasActiveHandoff && (
        <PopupMenuItem onClick={handleCreateHandoff}>Create</PopupMenuItem>
      )}
      {isEditable && (
        <PopupMenuItem isDelete onClick={remove}>
          Remove
        </PopupMenuItem>
      )}

      {hasActiveHandoff && (
        <FormHandoffModal
          defaultValues={selectedHandoff}
          hasCancelButton={isEditable}
          open={handoffModal.value}
          readonly={!isEditable}
          size="md"
          streamSchema={UpdateHandoffSchema}
          title={isEditable ? 'Edit Handoff' : 'View Handoff'}
          nested
          onCancelButtonClick={handleClose}
          onConfirmClicked={async data => {
            if (isEditable) {
              await serverDispatch(updateHandoff(data));
            }
            handleClose();
          }}
        />
      )}

      <FormHandoffModal
        buttonText="Create"
        open={handoffCreateModal.value}
        size="md"
        streamSchema={CreateHandoffSchema}
        title={HANDOFF.modal.add.title}
        onCancelButtonClick={handoffCreateModal.setFalse}
        onConfirmClicked={handleCreateHandoffConfig}
        onConfirmDDActionName={getActionName(
          'Handoffs',
          'create',
          undefined,
          'complete',
        )}
      />
    </>
  );
};

export const useHandoffActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isHandoffEnabled } = useActionsContext();
  const activeHandoffs = useAppSelector(selectActiveHandoffs);

  const handoff = activeHandoffs.find(h => h.id === id);

  const warning = !activeHandoffs?.some(t => t.id === id)
    ? WarningMessages.HANDOFF_NOT_EXIST
    : undefined;

  return {
    Content: HandoffTagContent,
    Icon: NodeTransferIcon,
    name: handoff?.name ?? id,
    warning: warning,
    prefix: HANDOFF_PREFIX,
    isEnabled: isHandoffEnabled,
    isLoading: false,
  };
};
